<script setup lang="ts">
import CustomRichTextCell from "@/components/mobile/modules/CustomRichTextCell.vue";
import ThreadsNav from "@/components/mobile/ThreadsNav.vue";
import ModalFriendMateSelect from "@/components/mobile/mate/ModalFriendMateSelect.vue";
import { ApiGetMateRelationshipPage } from "@/apis/interaction";
import { useMate } from "@/stores/mate";
import { useUI } from "@/stores/ui";
import { Mate, MateMail } from "@/types/entity";
import { showFailToast, Popup } from "vant";
import { ref } from "vue";
import { AIWriteType } from "@/enums/mateEnums";
import { useRoute } from "vue-router";

const props = withDefaults(
  defineProps<{
    show: boolean;
    mateMail: MateMail;
    enableSelectSender: boolean;
    enableSelectRecipient: boolean;
  }>(),
  {
    enableSelectSender: true,
    enableSelectRecipient: true,
  }
);

const emit = defineEmits<{
  (e: "update:show", show: boolean): void;
  (e: "save", mateMail: MateMail): void;
}>();

const mateMail = ref<MateMail>(props.mateMail);
const route = useRoute();
const mateStore = useMate();

const onContentUpdate = (content: string) => {
  mateMail.value.body = content;
};

const loading = ref(false);
const uiStore = useUI();
const showFriendMateSelect = ref(false);
const friendMateList = ref<Mate[]>([]);
const clickMateSelect = () => {
  if (!props.enableSelectSender) return;
  if (props.mateMail.parent) return showFailToast("回复信件，不能选择发件人");
  uiStore.popMateSelector({
    mateOnly: true,
    switchMate: false,
    exclude: [props.mateMail.senderId],
    title: "选择当前身份",
    callback: async (mate: Mate) => {
      uiStore.showMateSelector = false;
      mateMail.value.senderId = mate.id;
      loading.value = true;
      const { page } = await ApiGetMateRelationshipPage({
        pagination: {
          page: 1,
          perPage: 999,
        },
        filter: {
          targetType: "others",
          sourceMateId: mate.id,
        },
      });
      friendMateList.value = page.data.map((item) => item.targetMate!);
      loading.value = false;
    },
  });
};

const clickFriendMateSelect = () => {
  if (!props.enableSelectRecipient) return;
  if (props.mateMail.parent) return showFailToast("回复信件，不能选择收件人");
  showFriendMateSelect.value = true;
};

const onFriendMateSelect = (mate: Mate) => {
  console.log(mate);
  mateMail.value.recipientId = mate.id;
  mateMail.value.recipient = mate;
  showFriendMateSelect.value = false;
};

const onSave = () => {
  const { senderId, recipientId, subject, body } = props.mateMail;

  if (!senderId) return showFailToast("请选择发件人");
  if (!recipientId) return showFailToast("请选择收件人");
  if (!subject) return showFailToast("请输入标题");
  if (!body) return showFailToast("请输入信件内容");

  emit("save", mateMail.value);
  emit("update:show", false);
};
</script>
<template>
  <Popup
    :show="show"
    @update:show="emit('update:show', $event)"
    class="grid grid-rows-[auto_1fr] overflow-hidden h-[80%]"
    position="bottom"
    :class="{
      'pb-[var(--bottom-nav-height)]': route.meta.showBottomNav,
    }"
  >
    <ThreadsNav show-back :show-center="false">
      <template #right>
        <button class="btn btn-outline btn-sm" @click="onSave">
          {{ $t(`Buttons.save`) }}
        </button>
      </template>
    </ThreadsNav>
    <div class="h-full overflow-y-auto">
      <VanCellGroup inset>
        <!-- 名字 -->
        <VanField
          label="发件人"
          :model-value="mateStore.mateIdMap.get(mateMail.senderId)?.name"
          :placeholder="$t('mail.selectSenderFirst')"
          :rules="[
            { required: true, message: $t('User.mate.basicInfo.rules.name') },
          ]"
          required
          is-link
          readonly
          :disabled="!enableSelectSender"
          @click="clickMateSelect"
        />
        <VanField
          label="收件人"
          :model-value="mateMail.recipient?.name"
          :placeholder="$t('mail.selectRecipientFirst')"
          :rules="[
            { required: true, message: $t('User.mate.basicInfo.rules.name') },
          ]"
          required
          :is-link="!loading"
          :disabled="loading || !enableSelectRecipient"
          readonly
          @click="clickFriendMateSelect"
        >
          <template #right-icon v-if="loading">
            <i class="loading loading-ring loading-xs"></i>
          </template>
        </VanField>

        <!-- 标题 -->
        <VanField
          label="标题"
          v-model="mateMail.subject"
          :placeholder="$t('mail.subject')"
          required
        />

        <CustomRichTextCell
          title="填写信件内容"
          :ai-type="AIWriteType.MateMail"
          :content="mateMail.body"
          :cb="onContentUpdate"
          :cb2="(title:string) => (mateMail.subject = title)"
          :options="{
            senderId: mateMail.senderId,
            recipientId: mateMail.recipientId,
            subject: mateMail.subject,
          }"
        />
      </VanCellGroup>
    </div>

    <ModalFriendMateSelect
      v-model:show="showFriendMateSelect"
      :list="friendMateList"
      @select="onFriendMateSelect"
    />
  </Popup>
</template>
